/* Midnight translation bridge reader (layout/theme.liquid #mw-strings).
   Falls back to the English argument if the bridge is unavailable. Defined
   defensively because several theme scripts share it. */
window.mwString = window.mwString || function (key, fallback) {
  return window.mwT ? window.mwT(key, fallback) : fallback;
};
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-mw-watch-catalogue]';
  const FILTER_PREFIX = 'filter.';
  const AVAILABILITY_FILTER = 'filter.v.availability';
  const DEFAULT_SORT = 'created-descending';
  const PREVIEW_PARAMS = ['preview_theme_id', '_fd', 'pb'];

  const positiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  class MidnightWatchCatalogue {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.availableUrl = root.dataset.availableUrl;
      this.soldUrl = root.dataset.soldUrl;
      this.results = root.querySelector('[data-mw-catalogue-results]');
      this.status = root.querySelector('[data-mw-catalogue-status]');
      this.error = root.querySelector('[data-mw-catalogue-error]');
      this.filters = root.querySelector('[data-mw-catalogue-filters]');
      this.verticalSort = root.querySelector('[data-mw-catalogue-sort]');
      this.abortController = null;
      this.refreshToken = 0;
      this.debounceTimer = null;
      this.availableIds = new Set();
      this.currentSharedParams = '';
      this.soldPage = 1;

      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.enhance();
    }

    enhance() {
      this.root.dataset.mwCatalogueReady = 'true';
      this.root.classList.add('is-enhanced');
      if (this.filters) this.filters.hidden = false;
      if (this.verticalSort) this.verticalSort.hidden = false;

      const fallbackPagination = this.root.querySelector('[data-mw-fallback-pagination]');
      if (fallbackPagination) fallbackPagination.hidden = true;

      this.reconcileInitialAvailableCount();
      this.updatePaginationFromGrid();
      this.syncCatalogueVisibility();

      this.root.addEventListener('input', this.onInput);
      this.root.addEventListener('submit', this.onSubmit);
      this.root.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      const state = this.readUrlState();
      this.soldPage = state.soldPage;
      this.currentSharedParams = state.shared.toString();

      if (state.hadLegacyPage) {
        this.writeUrl(state.shared, state.soldPage, 'replace');
      }

      this.refresh(state.shared, state.soldPage, { updateControls: true, updateAvailable: true });
    }

    readUrlState() {
      const current = new URLSearchParams(window.location.search);
      const hadLegacyPage = current.has('page') && !current.has('mw_sold_page');
      const soldPage = positiveInteger(current.get('mw_sold_page') || current.get('page'), 1);

      return {
        shared: this.sharedParamsFrom(current),
        soldPage,
        hadLegacyPage,
      };
    }

    sharedParamsFrom(params) {
      const shared = new URLSearchParams();
      params.forEach((value, key) => {
        if (!value) return;
        if (key === 'sort_by') {
          shared.set(key, value);
        } else if (key.startsWith(FILTER_PREFIX)) {
          shared.append(key, value);
        }
      });
      return shared;
    }

    collectFormState(origin) {
      const originForm = origin.closest('form');
      const isMobile = originForm && originForm.id === 'FacetFiltersFormMobile';
      const forms = isMobile
        ? [originForm]
        : Array.from(
            this.root.querySelectorAll(
              '#FacetSortForm, #FacetFiltersForm, #FacetSortDrawerForm'
            )
          );
      const params = new URLSearchParams();

      forms.filter(Boolean).forEach((form) => {
        new FormData(form).forEach((value, key) => {
          const normalized = String(value).trim();
          if (!normalized) return;
          if (key === 'sort_by') {
            params.set(key, normalized);
          } else if (key.startsWith(FILTER_PREFIX)) {
            params.append(key, normalized);
          }
        });
      });

      return params;
    }

    onInput(event) {
      if (!event.target.closest('[data-mw-catalogue-filters], [data-mw-catalogue-sort]')) return;
      this.validatePriceRange(event.target);
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        this.applySharedState(this.collectFormState(event.target), event.target);
      }, 450);
    }

    onSubmit(event) {
      if (!event.target.closest('[data-mw-catalogue-filters], [data-mw-catalogue-sort]')) return;
      event.preventDefault();
      window.clearTimeout(this.debounceTimer);
      this.applySharedState(this.collectFormState(event.target), event.submitter || event.target);
    }

    onClick(event) {
      const clear = event.target.closest('[data-mw-catalogue-clear]');
      if (clear && this.root.contains(clear)) {
        event.preventDefault();
        this.applySharedState(new URLSearchParams(), clear);
        return;
      }

      const previous = event.target.closest('[data-mw-sold-previous]');
      const next = event.target.closest('[data-mw-sold-next]');

      if (previous || next) {
        event.preventDefault();
        const delta = previous ? -1 : 1;
        const nextPage = Math.max(1, this.soldPage + delta);
        const shared = this.sharedParamsFrom(new URLSearchParams(window.location.search));
        this.writeUrl(shared, nextPage, 'push');
        this.refresh(shared, nextPage, {
          updateControls: false,
          updateAvailable: false,
          focusSold: true,
        });
        return;
      }

      const removeLink = event.target.closest(
        '[data-mw-catalogue-filters] .js-facet-remove a, [data-mw-catalogue-filters] .active-facets__button-remove, [data-mw-catalogue-filters] facet-remove a'
      );
      if (!removeLink) return;

      event.preventDefault();
      const targetUrl = new URL(removeLink.href, window.location.origin);
      this.applySharedState(this.sharedParamsFrom(targetUrl.searchParams), removeLink);
    }

    onPopState() {
      const state = this.readUrlState();
      this.refresh(state.shared, state.soldPage, { updateControls: true, updateAvailable: true });
    }

    applySharedState(shared, origin) {
      this.writeUrl(shared, 1, 'push');
      this.refresh(shared, 1, {
        updateControls: true,
        updateAvailable: true,
        origin,
      });
    }

    writeUrl(shared, soldPage, method) {
      const url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key === 'page' ||
          key === 'mw_sold_page' ||
          key === 'sort_by' ||
          key.startsWith(FILTER_PREFIX)
        ) {
          url.searchParams.delete(key);
        }
      });

      shared.forEach((value, key) => url.searchParams.append(key, value));
      if (soldPage > 1) url.searchParams.set('mw_sold_page', String(soldPage));

      window.history[`${method}State`](
        { mwWatchCatalogue: true, searchParams: url.searchParams.toString() },
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    }

    async refresh(shared, soldPage, options = {}) {
      const settings = {
        updateControls: true,
        updateAvailable: true,
        focusSold: false,
        origin: null,
        ...options,
      };

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const refreshToken = ++this.refreshToken;
      this.setLoading(true);
      this.hideError();

      try {
        const availablePromise = settings.updateAvailable
          ? this.loadAllAvailable(shared, signal)
          : Promise.resolve(null);
        const soldPromise = this.loadSoldPage(shared, soldPage, signal);
        const controlsPromise = settings.updateControls
          ? this.fetchControls(shared, signal)
          : Promise.resolve(null);

        const [available, sold, controls] = await Promise.all([
          availablePromise,
          soldPromise,
          controlsPromise,
        ]);

        if (signal.aborted || refreshToken !== this.refreshToken) return;

        if (available) {
          this.renderFeed('available', available.items, available.total);
        }

        this.renderFeed('sold', sold.items, sold.total);
        this.soldPage = sold.page;
        this.renderSoldPagination(sold.page, sold.pages);
        this.syncCatalogueVisibility();

        if (controls) this.renderControls(controls, settings.origin);

        this.currentSharedParams = shared.toString();
        if (sold.page !== soldPage) this.writeUrl(shared, sold.page, 'replace');
        this.announceCurrentCounts();

        if (settings.focusSold) this.focusSoldHeading();
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (refreshToken !== this.refreshToken) return;
        this.renderFailureState();
        this.showError(mwString('facets.catalogue_failed', 'We couldn\u2019t update the results. Clear the filters and try again.'));
      } finally {
        if (!signal.aborted && refreshToken === this.refreshToken) this.setLoading(false);
      }
    }

    async loadAllAvailable(shared, signal) {
      const first = await this.fetchFeedPage(this.availableUrl, shared, 1, 'available', signal);

      const pageRequests = [];
      for (let page = 2; page <= first.pages; page += 1) {
        pageRequests.push(this.fetchFeedPage(this.availableUrl, shared, page, 'available', signal));
      }

      const remaining = await Promise.all(pageRequests);
      return {
        items: [first, ...remaining].flatMap((entry) => entry.items),
        total: first.total,
        pages: first.pages,
      };
    }

    async loadSoldPage(shared, requestedPage, signal) {
      let result = await this.fetchFeedPage(this.soldUrl, shared, requestedPage, 'sold', signal);
      const safePage = result.pages > 0 ? Math.min(requestedPage, result.pages) : 1;
      if (safePage !== requestedPage) {
        result = await this.fetchFeedPage(this.soldUrl, shared, safePage, 'sold', signal);
      }
      return { ...result, page: safePage };
    }

    async fetchFeedPage(path, shared, page, expectedFeed, signal) {
      const feedParams = this.paramsForFeed(shared, expectedFeed);
      if (!feedParams) {
        return { items: [], total: 0, page: 1, pages: 0 };
      }

      const response = await fetch(this.sectionUrl(path, feedParams, page), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
        signal,
      });
      if (!response.ok) throw new Error(`Catalogue request failed with ${response.status}.`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = documentFragment.getElementById('product-grid');
      if (!grid) throw new Error('Catalogue response did not include a product grid.');
      if (grid.dataset.mwFeed !== expectedFeed) {
        throw new Error(`Catalogue response did not match the ${expectedFeed} source.`);
      }

      const items = Array.from(grid.querySelectorAll('[data-mw-product-id]'));
      const expectedAvailability = expectedFeed === 'available' ? 'true' : 'false';
      if (
        items.some(
          (item) =>
            item.dataset.mwProductState !== expectedFeed ||
            item.dataset.mwProductAvailable !== expectedAvailability
        )
      ) {
        throw new Error(`Catalogue response contained an invalid ${expectedFeed} product.`);
      }

      return {
        items,
        total: Math.max(0, Number.parseInt(grid.dataset.mwTotalCount || '0', 10) || 0),
        page: positiveInteger(grid.dataset.mwCurrentPage, 1),
        pages: Math.max(0, Number.parseInt(grid.dataset.mwTotalPages || '0', 10) || 0),
      };
    }

    paramsForFeed(shared, expectedFeed) {
      const expectedValue = expectedFeed === 'available' ? '1' : '0';
      const selectedAvailability = shared.getAll(AVAILABILITY_FILTER);
      if (selectedAvailability.length && !selectedAvailability.includes(expectedValue)) return null;

      const scoped = new URLSearchParams();
      shared.forEach((value, key) => {
        if (key !== AVAILABILITY_FILTER) scoped.append(key, value);
      });
      scoped.append(AVAILABILITY_FILTER, expectedValue);
      return this.withDefaultSort(scoped);
    }

    withDefaultSort(params) {
      const scoped = new URLSearchParams(params);
      if (!scoped.has('sort_by')) scoped.set('sort_by', DEFAULT_SORT);
      return scoped;
    }

    async fetchControls(shared, signal) {
      const response = await fetch(
        this.sectionUrl(window.location.pathname, this.withDefaultSort(shared), 1),
        {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
          signal,
        }
      );
      if (!response.ok) throw new Error(`Filter request failed with ${response.status}.`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    sectionUrl(path, shared, page) {
      const url = new URL(path, window.location.origin);
      const current = new URL(window.location.href);
      shared.forEach((value, key) => url.searchParams.append(key, value));
      PREVIEW_PARAMS.forEach((key) => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', String(page));
      return url.toString();
    }

    renderFeed(feed, sourceItems, total) {
      const grid = this.root.querySelector(`[data-mw-catalogue-grid="${feed}"]`);
      const section = this.root.querySelector(`[data-mw-catalogue-feed="${feed}"]`);
      const seen = new Set();
      const items = [];

      sourceItems.forEach((source) => {
        const id = source.dataset.mwProductId;
        if (!id || seen.has(id)) return;
        if (source.dataset.mwProductState !== feed) return;
        seen.add(id);
        items.push(document.importNode(source, true));
      });

      grid.replaceChildren(...items);
      grid.dataset.mwTotalCount = String(total);
      grid.hidden = total === 0;
      if (section) section.hidden = total === 0;

      const count = this.root.querySelector(`[data-mw-catalogue-count="${feed}"]`);
      if (count) count.textContent = String(total);
    }

    syncCatalogueVisibility({ showEmpty = true } = {}) {
      const counts = ['available', 'sold'].map((feed) => {
        const node = this.root.querySelector(`[data-mw-catalogue-count="${feed}"]`);
        return Math.max(0, Number.parseInt(node?.textContent || '0', 10) || 0);
      });
      const empty = this.root.querySelector('[data-mw-catalogue-empty]');
      if (empty) empty.hidden = !showEmpty || counts.some((count) => count > 0);
    }

    renderFailureState() {
      this.renderFeed('available', [], 0);
      this.renderFeed('sold', [], 0);
      this.renderSoldPagination(1, 0);
      this.syncCatalogueVisibility({ showEmpty: false });
      this.announceCurrentCounts();
    }

    reconcileInitialAvailableCount() {
      const grid = this.root.querySelector('[data-mw-catalogue-grid="available"]');
      const count = this.root.querySelector('[data-mw-catalogue-count="available"]');
      if (!grid || !count) return;
      const rendered = grid.querySelectorAll('[data-mw-product-id]').length;
      grid.dataset.mwTotalCount = String(rendered);
      count.textContent = String(rendered);
    }

    renderControls(sourceDocument, origin) {
      if (!this.filters) return;
      const nextFilters = sourceDocument.querySelector('[data-mw-catalogue-filters]');
      if (!nextFilters) throw new Error('Filter response did not include shared controls.');

      const controlState = this.captureControlState(origin);
      this.filters.innerHTML = nextFilters.innerHTML;
      this.filters.hidden = false;
      this.restoreControlState(controlState);

      if (this.verticalSort) {
        const nextSort = sourceDocument.querySelector('[data-mw-catalogue-sort]');
        if (nextSort) this.verticalSort.innerHTML = nextSort.innerHTML;
        this.verticalSort.hidden = false;
      }
    }

    captureControlState(origin) {
      const activeElement = origin || document.activeElement;
      return {
        focusId: activeElement && activeElement.id ? activeElement.id : '',
        openDetails: Array.from(this.filters.querySelectorAll('details[open]')).map((details) => ({
          id: details.id,
          index: details.dataset.index,
        })),
      };
    }

    restoreControlState(state) {
      state.openDetails.forEach((entry) => {
        let target = null;
        if (entry.id) {
          const byId = document.getElementById(entry.id);
          if (byId && this.filters.contains(byId)) target = byId;
        }
        if (!target) {
          target = Array.from(this.filters.querySelectorAll('details')).find(
            (details) => details.dataset.index === entry.index
          );
        }
        if (target) target.open = true;
      });

      if (state.focusId) {
        const byId = document.getElementById(state.focusId);
        const focusTarget = byId && this.root.contains(byId) ? byId : null;
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }
    }

    renderSoldPagination(page, pages) {
      const controls = this.root.querySelector('[data-mw-sold-pagination-controls]');
      if (!controls) return;
      controls.hidden = pages <= 1;

      const previous = controls.querySelector('[data-mw-sold-previous]');
      const next = controls.querySelector('[data-mw-sold-next]');
      const current = controls.querySelector('[data-mw-sold-current-page]');
      const total = controls.querySelector('[data-mw-sold-total-pages]');

      if (previous) previous.disabled = page <= 1;
      if (next) next.disabled = pages === 0 || page >= pages;
      if (current) current.textContent = String(page);
      if (total) total.textContent = String(pages);
    }

    updatePaginationFromGrid() {
      const grid = this.root.querySelector('[data-mw-catalogue-grid="sold"]');
      if (!grid) return;
      this.soldPage = positiveInteger(grid.dataset.mwCurrentPage, 1);
      this.renderSoldPagination(
        this.soldPage,
        Math.max(0, Number.parseInt(grid.dataset.mwTotalPages || '0', 10) || 0)
      );
    }

    idsFromGrid(feed) {
      return new Set(
        Array.from(
          this.root.querySelectorAll(
            `[data-mw-catalogue-grid="${feed}"] [data-mw-product-id]`
          )
        ).map((item) => item.dataset.mwProductId)
      );
    }

    validatePriceRange(target) {
      const range = target.closest('price-range');
      if (!range) return;
      const inputs = Array.from(range.querySelectorAll('input'));
      if (inputs.length < 2) return;
      const [minimum, maximum] = inputs;
      if (maximum.value) minimum.max = maximum.value;
      if (minimum.value) maximum.min = minimum.value;
    }

    focusSoldHeading() {
      const heading = this.root.querySelector('[data-mw-catalogue-feed="sold"] h2');
      if (!heading) return;
      window.requestAnimationFrame(() => {
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({ block: 'start' });
      });
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      if (this.results) this.results.setAttribute('aria-busy', String(isLoading));
      if (isLoading && this.status) this.status.textContent = mwString('facets.catalogue_updating', 'Updating catalogue results.');
    }

    announceCurrentCounts() {
      if (!this.status) return;
      const available = this.root.querySelector('[data-mw-catalogue-count="available"]');
      const sold = this.root.querySelector('[data-mw-catalogue-count="sold"]');
      this.status.textContent = `${available ? available.textContent : '0'} available and ${
        sold ? sold.textContent : '0'
      } sold watches.`;
    }

    showError(message) {
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = false;
      }
      if (this.status) this.status.textContent = message;
    }

    hideError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (root.dataset.mwCatalogueReady === 'true') return;
    new MidnightWatchCatalogue(root);
  });
})();
